import { z } from "zod";
import { idSchema, idempotencyKeySchema } from "./common.js";
import { tierSchema } from "./tier.js";

/**
 * Phase 12f — input/output schemas for the `reports.*` procedures
 * (legend / detailed-report / label-sheet / listAvailable). The
 * generators wrap the Phase 7b/7d document builders plus the Phase 11
 * PDF renderers; the API surface here is the network contract the
 * Phase 13 client consumes.
 *
 * The legend procedure is **idempotent over `lastLegendHash`** — a
 * regenerate with no panel changes returns the existing R2 object's
 * presigned URL — so the input does *not* carry an idempotency key;
 * the hash *is* the dedupe handle. The detailed-report and label-sheet
 * procedures stream the PDF back inline (no R2 persistence layer at
 * MVP), so they also skip the idempotency envelope. The four
 * mutating-write procedures from 12d carry idempotency for *replay
 * safety* — these reports are read-shaped queries.
 *
 * Inputs are kept thin: aggregate ids plus a small set of presentation
 * knobs the procedure cannot derive. Anything that depends on the
 * underlying graph (the device's circuit memberships, the panel's
 * manufacturer, etc.) is resolved server-side by the procedure body.
 */

/**
 * Stable keys for the legend page geometries 12f exposes. The user-
 * facing strings mirror the manufacturer brands the Phase 11b
 * renderer geometries cover; the procedure body maps each key to the
 * Phase 7b `LegendPreset` + Phase 11b `PageGeometry` pair.
 *
 * `generic-letter-a4` is the brand-neutral letter geometry per the
 * Phase 12f spec ("one of `squareD-homeline` / `siemens` /
 * `generic-letter-a4`"); the renderer ships an A4 variant too, but
 * 12f's MVP picker only exposes the letter form.
 */
export const legendPresetKindSchema = z.enum([
  "squareD-homeline",
  "siemens",
  "generic-letter-a4",
]);
export type LegendPresetKind = z.infer<typeof legendPresetKindSchema>;

export const reportsGenerateLegendInputSchema = z.object({
  panelId: idSchema,
  preset: legendPresetKindSchema,
});
export type ReportsGenerateLegendInput = z.infer<
  typeof reportsGenerateLegendInputSchema
>;

/**
 * Output of `reports.generateLegend`. `presignedUrl` is the 5-minute
 * presigned GET URL pointing at the R2 object; `ttlSeconds` lets the
 * Phase 13 client know when to refresh on tab focus; `hash` is the
 * legend content hash the procedure stamped on the panel (and the
 * idempotency-by-hash key).
 */
export const reportsGenerateLegendOutputSchema = z.object({
  presignedUrl: z.string().url(),
  ttlSeconds: z.number().int().positive(),
  hash: z.string().min(1),
  /**
   * `true` when the procedure re-used an existing R2 artifact (no
   * re-render, no panel re-stamp); `false` when the procedure
   * rendered fresh bytes. Lets the Phase 13 surface render a "you
   * already generated this" hint vs the default "generating…" spinner.
   */
  reused: z.boolean(),
});
export type ReportsGenerateLegendOutput = z.infer<
  typeof reportsGenerateLegendOutputSchema
>;

/** Page size knob shared by the detailed-report procedure. */
export const reportPageSizeSchema = z.enum(["letter", "a4"]);
export type ReportPageSize = z.infer<typeof reportPageSizeSchema>;

export const reportsGenerateDetailedReportInputSchema = z.object({
  panelId: idSchema,
  pageSize: reportPageSizeSchema.default("letter"),
});
export type ReportsGenerateDetailedReportInput = z.infer<
  typeof reportsGenerateDetailedReportInputSchema
>;

/**
 * Output of `reports.generateDetailedReport` /
 * `reports.generateLabelSheet`. The PDF is streamed back inline as a
 * base64-encoded buffer; the Phase 13 client decodes via `atob`. The
 * procedure does *not* persist a copy to R2 because the source
 * documents may include freshly-uploaded photos whose stable hash is
 * not yet computed at MVP — a fresh render per request keeps the
 * contract simple.
 */
export const reportsInlinePdfOutputSchema = z.object({
  pdfBase64: z.string().min(1),
  contentType: z.literal("application/pdf"),
  byteLength: z.number().int().positive(),
});
export type ReportsInlinePdfOutput = z.infer<
  typeof reportsInlinePdfOutputSchema
>;

/**
 * Avery template keys 12f exposes. The Phase 7d registry ships the
 * Avery 5160 template at MVP; future Avery sheets are a one-line
 * append on both the domain registry and this enum.
 */
export const labelSheetTemplateKeySchema = z.enum(["avery-5160"]);
export type LabelSheetTemplateKey = z.infer<
  typeof labelSheetTemplateKeySchema
>;

export const reportsGenerateLabelSheetInputSchema = z.object({
  siteId: idSchema,
  deviceIds: z.array(idSchema).min(1).max(500),
  template: labelSheetTemplateKeySchema.default("avery-5160"),
});
export type ReportsGenerateLabelSheetInput = z.infer<
  typeof reportsGenerateLabelSheetInputSchema
>;

/**
 * One entry per `REPORT_REGISTRY` descriptor, projected for the
 * Reports hub. The hub renders this list directly; the `available`
 * arm matches the Phase 7d registry's `tierGate` against the caller's
 * tier (`true` when the tier is sufficient). The `implemented` flag
 * mirrors the registry's `generator !== undefined` — the Phase 13 UI
 * shows a "coming soon" hint when a tier-allowed report has no
 * generator wired (the Pro entries at MVP).
 */
export const reportRegistryEntrySchema = z.object({
  key: z.string().min(1),
  displayName: z.string().min(1),
  description: z.string().min(1),
  scope: z.enum(["panel", "site"]),
  tierGate: tierSchema,
  available: z.boolean(),
  implemented: z.boolean(),
});
export type ReportRegistryEntry = z.infer<typeof reportRegistryEntrySchema>;

export const reportsListAvailableOutputSchema = z.object({
  reports: z.array(reportRegistryEntrySchema),
});
export type ReportsListAvailableOutput = z.infer<
  typeof reportsListAvailableOutputSchema
>;
